import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import HeroImage25D from '../components/website/HeroImage25D';
import {
    Linkedin,
    Mail,
    Phone,
    Globe,
    ChevronLeft,
    Award,
    Code,
    Cpu
} from 'lucide-react';

const CeoPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-black text-white relative overflow-hidden selection:bg-cyan-500/30 font-sans">
            {/* Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-20%] right-[-10%] w-[70vw] h-[70vw] bg-cyan-500/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-blue-600/10 rounded-full blur-[150px]" />
                <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-20 [mask-image:radial-gradient(ellipse_at_center,white,transparent)]" />
            </div>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 p-6 z-50 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group">
                    <div className="p-2 rounded-full border border-white/10 group-hover:bg-white/10 transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-bold tracking-widest">BACK TO SAFETY GRID</span>
                </Link>
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center font-black text-black text-xs">N</div>
                </div>
            </nav>

            <main className="container mx-auto px-6 pt-32 pb-20 relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">

                    {/* LEFT: Image & Visuals */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="relative flex flex-col items-center"
                    >
                        {/* 2.5D Effect */}
                        <div className="relative w-full max-w-md aspect-square perspective-1000">
                            <HeroImage25D
                                src="/team/founder.png"
                                alt="Aymane Elamrani"
                                className="w-full h-full"
                            />

                            {/* Floating Badge */}
                            <motion.div
                                className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl flex items-center gap-4 min-w-[280px] shadow-2xl z-30"
                                initial={{ y: -20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400">
                                    <Award className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="text-xs text-slate-400 uppercase tracking-widest">Role</div>
                                    <div className="font-bold text-white">Founder & Lead Architect</div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* RIGHT: Content & Bio */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        <div>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="inline-block px-4 py-1 rounded-full border border-cyan-500/30 text-cyan-400 text-xs font-bold tracking-[0.2em] mb-4 bg-cyan-500/5"
                            >
                                THE VISIONARY
                            </motion.div>
                            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                                Aymane <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                                    Elamrani
                                </span>
                            </h1>
                            <p className="text-xl text-slate-300 leading-relaxed max-w-xl">
                                "We are not explicitly building an app. We are constructing the <span className="text-white font-bold">digital nervous system</span> for a safer world. Every line of code saves a life."
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex gap-4 items-start">
                                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                                    <Code className="text-cyan-400 w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-lg">Full-Stack Architect</h3>
                                    <p className="text-slate-400 text-sm mt-1">
                                        Expert in Rust, React, and Distributed Systems. Over 9 years of experience building scalable platforms serving millions.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4 items-start">
                                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                                    <Cpu className="text-purple-400 w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-lg">AI & Embedded Systems</h3>
                                    <p className="text-slate-400 text-sm mt-1">
                                        Pioneering Edge AI solutions that run on local hardware, ensuring privacy and offline capability.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Contact Actions */}
                        <div className="flex flex-wrap gap-4 pt-4 border-t border-white/10">
                            <a
                                href="mailto:aymane@zelli.space"
                                className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white text-black font-bold hover:bg-cyan-400 transition-colors"
                            >
                                <Mail className="w-5 h-5" />
                                Email Founder
                            </a>
                            <a
                                href="https://linkedin.com/in/aymane-elamrani"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-colors"
                            >
                                <Linkedin className="w-5 h-5" />
                                LinkedIn
                            </a>
                            <a
                                href="tel:+2126767078753"
                                className="flex items-center gap-3 px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-colors"
                            >
                                <Phone className="w-5 h-5" />
                                Call
                            </a>
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
};

export default CeoPage;
